import { zepboundPenDrugKey, zepboundVialDrugKey } from '../constants';
import {
    useDrugProgram,
    useMedicationConfigOverride,
} from '../contexts/DrugProgramProvider';
import type {
    AuthorizedMedicationInfoFragment,
    MedicationInfoFragment,
    UnknownMedicationInfoFragment,
} from '../graphql/generated/graphql';
import type { NdcDefinition } from '../types/drug-config';
import { formatToLocalizedCurrency } from '../util';
import { getDrugConfigByNdc } from '../util/config/get-drug-config-by-medication';
import type { PricingInfo } from '../util/config/get-pricing';
import { getSavingsAmountByNdc } from '../util/config/get-savings-amount';

type MedicationParam =
    | AuthorizedMedicationInfoFragment
    | MedicationInfoFragment
    | UnknownMedicationInfoFragment;

export const getNdc = (
    medication?: MedicationParam,
    ndcDefinition?: NdcDefinition
): string | undefined => {
    if (ndcDefinition) {
        return ndcDefinition.ndc;
    }

    if (!medication) {
        return undefined;
    }

    return medication.__typename === 'UnknownMedication' &&
        medication.originalNdc
        ? medication.originalNdc
        : medication.id;
};

export const useMedicationPricing = (
    pricingInfo: PricingInfo,
    medication?: MedicationParam,
    ndcDefinition?: NdcDefinition
) => {
    const drugProgram = useDrugProgram();
    const configOverride = useMedicationConfigOverride();

    const price =
        typeof pricingInfo.price === 'number'
            ? formatToLocalizedCurrency(pricingInfo.price)
            : pricingInfo.price;

    const ndc = getNdc(medication, ndcDefinition);
    const drugConfig = getDrugConfigByNdc(drugProgram, ndc);
    const isOgZepbound =
        drugConfig?.drugKey === zepboundVialDrugKey ||
        drugConfig?.drugKey === zepboundPenDrugKey;

    const savingsAmount =
        ndc !== undefined
            ? getSavingsAmountByNdc(ndc, pricingInfo.price, configOverride)
            : 0;

    return {
        price,
        isOgZepbound,
        savingsAmount,
    };
};
